import * as React from "react";
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartCategoryAxisLabels,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartValueAxisItemProps,
} from "@progress/kendo-react-charts";
import { currencyFormatterV2, useToggableLegendV2 } from "@iliotech/data-wire";
import s from "./ColumnChart.module.scss";
import { Empty } from "@iliotech/widgets";
import { tooltipRenderer } from "../TooltipRenderer/TooltipRenderer";
import { ToggableChartLegend } from "../ToggableChartLegend/ToggableChartLegend";
import { useCurrentWidth } from "@iliotech/data-wire";
import { IChartDataItemV2 } from "@iliotech/types";

const MAX_CATEGORY_LABEL_LENGTH = 22;
interface IProps {
  data: IChartDataItemV2[];
  onSelectPosition?: (item: any) => void;
  valueFormatter?: (value: number) => string;
  maxBars?: number;
  hideLegend?: boolean;
  valueLabel?: string;
  withAnimations?: boolean;
  chartValueAxisItemProps?: ChartValueAxisItemProps;
  generating?: boolean;
  height?: number;
  passedWidth?: number;
  maxCategoryLabelLength?: number;
  renderEmpty?: JSX.Element;
}

export const ColumnChart = ({
  data,
  valueFormatter = (value: number) => currencyFormatterV2(value || 0, 0, 0),
  onSelectPosition,
  maxBars,
  hideLegend,
  valueLabel,
  withAnimations = false,
  chartValueAxisItemProps,
  generating = false,
  height,
  passedWidth,
  maxCategoryLabelLength = MAX_CATEGORY_LABEL_LENGTH,
}: IProps) => {
  const { legend, onLegendItemClick, toggledData } = useToggableLegendV2({
    valueFormatter,
    chartData: data,
    toggableTotal: false,
  });

  const pageWidth = useCurrentWidth();
  const width = passedWidth || pageWidth;
  const barLimits = maxBars || (width >= 1366 ? 25 : width > 768 ? 15 : 10);

  if (!data?.length) {
    return <Empty />;
  }
  return (
    <div>
      <Chart
        style={{ height: height, width: passedWidth }}
        transitions={withAnimations}
        onSeriesClick={(...e) => {
          onSelectPosition?.({
            ...e,
          });
        }}
        className={s.chartContainer}
      >
        <ChartTooltip
          opacity={0.9}
          background={"var(--dark)"}
          shared={true}
          render={(context) =>
            tooltipRenderer(context, valueFormatter, data, valueLabel)
          }
        />
        <ChartArea
          background={"transparent"}
          width={passedWidth}
          height={height}
        />
        <ChartValueAxis>
          <ChartValueAxisItem
            background={"transparent"}
            majorGridLines={{
              color: generating ? undefined : "var(--color-lighter)",
            }}
            {...chartValueAxisItemProps}
          >
            <ChartCategoryAxisLabels
              color={"var(--color)"}
              content={(e) => valueFormatter(e.value)}
            />
          </ChartValueAxisItem>
        </ChartValueAxis>
        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            majorGridLines={{
              color: generating ? undefined : "var(--color-lighter)",
            }}
            labels={{
              rotation: "auto",
              color: "var(--color)",
              position: "start",

              content: (e) => {
                const withCustodianRemoved = e.value.split("[")[0];
                return withCustodianRemoved.length > maxCategoryLabelLength
                  ? withCustodianRemoved.substr(0, maxCategoryLabelLength) +
                      "..."
                  : withCustodianRemoved;
              },
            }}
            max={data.length > barLimits ? barLimits : data.length}
          />
        </ChartCategoryAxis>
        <ChartSeries>
          <ChartSeriesItem
            stack={true}
            border={{
              width: 0.1,
            }}
            width={0.1}
            data={toggledData}
            aggregate={"sum"}
            categoryField="name"
          />
        </ChartSeries>
      </Chart>
      {!hideLegend && (
        <ToggableChartLegend legend={legend} onItemPress={onLegendItemClick} />
      )}
    </div>
  );
};
